<script lang="ts">
  import { onMount } from "svelte";
  import { dialogTypes } from "../../lib/constants";
  import { dialogData } from "../../stores/dialogDataStore";
  import { signCloudflareImage } from "../../lib/cloudflare/uploadImageCloudflare";
  import type { IMessage } from "src/interfaces/Chat";
  import { parseDateWithTime, translate } from "lib";
  import { user } from "stores";

  export let message: IMessage;
  export let isMine: boolean;
  export let scrollToBottom = () => {};
  export let isBottomImage = false;
  export let sharedSenderLabel: string = "";
  export let isSharedTrainerChatEnabled: boolean = false;
  export let onHistoryOpen: (message: IMessage) => void = () => {};

  export let touchstart: (
    messageId: number,
    messagePayload: string,
    timestamp: any,
    isMine: boolean,
    deletedAt: string | null
  ) => void;
  export let touchend: () => void;

  let imgUrl = "";
  let showTimestamp = false;
  const editHistoryThresholdMs = 500;

  const toggleTimestamp = (): void => {
    showTimestamp = !showTimestamp;
  };

  const hasEditHistory = (): boolean => {
    const createdAt = new Date(message.createdAt).getTime();
    const updatedAt = new Date(message.updatedAt).getTime();

    if (Number.isNaN(createdAt) || Number.isNaN(updatedAt)) {
      return false;
    }

    return updatedAt - createdAt > editHistoryThresholdMs;
  };

  const canOpenHistory = (): boolean => {
    return Boolean(isSharedTrainerChatEnabled && (message.deletedAt || hasEditHistory()));
  };

  const openHistory = (event?: MouseEvent | KeyboardEvent): void => {
    if (!canOpenHistory()) {
      return;
    }

    event?.preventDefault();
    event?.stopPropagation();
    onHistoryOpen(message);
  };

  const handleDeletedMessageClick = (event: MouseEvent): void => {
    if (canOpenHistory()) {
      openHistory(event);
      return;
    }

    toggleTimestamp();
  };

  const handleDeletedMessageKeypress = (event: KeyboardEvent): void => {
    if (event.key !== "Enter" && event.key !== " ") {
      return;
    }

    event.preventDefault();

    if (canOpenHistory()) {
      openHistory(event);
      return;
    }

    toggleTimestamp();
  };

  const handleWrapperClick = (event: MouseEvent): void => {
    if (canOpenHistory()) {
      openHistory(event);
      return;
    }

    toggleTimestamp();
  };

  const handleWrapperKeypress = (event: KeyboardEvent): void => {
    if (event.key !== "Enter" && event.key !== " ") {
      return;
    }

    event.preventDefault();

    if (canOpenHistory()) {
      openHistory(event);
      return;
    }

    toggleTimestamp();
  };

  const stopPropagation = (event: MouseEvent | KeyboardEvent): void => {
    event.stopPropagation();
  };

  $: align = isMine ? "flex-end" : "flex-start";
  $: metadataText = sharedSenderLabel
    ? `${parseDateWithTime(message.createdAt)} - ${sharedSenderLabel}`
    : parseDateWithTime(message.createdAt);

  const onFullscreenVideo = (event?: MouseEvent): void => {
    event?.stopPropagation();
    $dialogData.data = {src: message?.payload};
    $dialogData.type = dialogTypes.VIDEO_ZOOM_V2;
  };

  onMount(async () => {
    if (message?.payload?.includes(":cloudflareimg:")) {
      imgUrl = await signCloudflareImage(message?.payload);
      return;
    }
  });
</script>

<!-- svelte-ignore a11y-media-has-caption -->
<!-- svelte-ignore a11y-no-static-element-interactions -->
{#if message?.deletedAt}
  <div
    unselectable="on"
    id={`${message?.id}`}
    on:click={handleDeletedMessageClick}
    on:keypress={handleDeletedMessageKeypress}
    role="button"
    tabindex="0"
    class={`deleted-message max-w-80 font-sans whitespace-pre-wrap mgl-8 mgr-8 mt-1 text-red-900 dark:text-red-200 bg-slate-100 dark:bg-zinc-800 dark:border dark:border-zinc-600`}
    style={`
  font-size: ${$user.id === 13 ? 13 : 14}px;
  -moz-user-select: none;
  -webkit-user-select: none;
  -ms-user-select: none;
  user-select: none;
  -o-user-select: none;
  align-self: ${align};`}
  >
    <p class="italic">{translate("DELETED_MESSAGE")}</p>
  </div>
  {#if showTimestamp}
    <div
      class="ml-3 mr-3 mt-1 flex items-center gap-2 text-xs font-medium text-slate-500 dark:text-slate-400"
      style={`align-self: ${align}`}
    >
      <span>{metadataText}</span>
    </div>
  {/if}
{:else}
  <!-- svelte-ignore a11y-no-noninteractive-tabindex -->
  <div
    class="message mgl-8 mgr-8 mt-2"
    style={`align-self: ${align};`}
    class:video-height={message?.payload?.includes("cloudflarestream")}
    class:image-height={message?.payload?.includes(":cloudflareimg:")}
    on:click={handleWrapperClick}
    on:keypress={handleWrapperKeypress}
    on:mousedown={() =>
      touchstart(message?.id, "FILE_MESSAGE", message?.createdAt, isMine, message?.deletedAt)}
    on:mouseup={() => touchend()}
    on:touchend={() => touchend()}
    on:touchstart={() =>
      touchstart(message?.id, "FILE_MESSAGE", message?.createdAt, isMine, message?.deletedAt)}
    role={canOpenHistory() ? "button" : undefined}
    tabindex={canOpenHistory() ? 0 : undefined}
  >
    {#if message?.payload && message?.payload.includes("cloudflarestream")}
      <!-- svelte-ignore a11y-click-events-have-key-events -->
      <div
        class="relative w-[256px] h-[150px] border rounded-md border-slate-200 dark:border-zinc-600"
        on:click={onFullscreenVideo}
      >
        <img
          src={message?.videoThumbnail}
          alt="Video thumbnail"
          class="absolute top-0 left-0 w-full h-full object-cover"
          loading="lazy"
          on:load={() => (isBottomImage === true ? scrollToBottom() : {})}
        />
        <div class="absolute inset-0 flex items-center justify-center">
          <svg
            class="w-12 h-12 text-white drop-shadow"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path d="M6 4l12 6-12 6V4z" />
          </svg>
        </div>
      </div>
    {:else if imgUrl !== ""}
      <!-- svelte-ignore a11y-click-events-have-key-events -->
      <div
        class="gallery-entry-image"
        role="button"
        tabindex="0"
        on:keypress={stopPropagation}
        on:click|stopPropagation={() => {
          $dialogData.data = { src: imgUrl };
          $dialogData.type = dialogTypes.IMAGE_ZOOM;
        }}
      >
        <img
          src={imgUrl}
          alt={"img"}
          class="image border rounded-md border-slate-200 dark:border-zinc-600"
          on:load={() => (isBottomImage === true ? scrollToBottom() : {})}
        />
      </div>
    {/if}
  </div>
  {#if showTimestamp}
    <!-- svelte-ignore a11y-no-noninteractive-tabindex -->
    <div
      class="ml-3 mr-3 mt-1 flex items-center gap-2 text-xs font-medium text-slate-500 dark:text-slate-400"
      style={`align-self: ${align}`}
      on:click={handleWrapperClick}
      on:keypress={handleWrapperKeypress}
      role={canOpenHistory() ? "button" : undefined}
      tabindex={canOpenHistory() ? 0 : undefined}
    >
      <span>{metadataText}</span>
    </div>
  {/if}
{/if}

<style>
  .deleted-message {
    align-self: flex-end;
    padding: 6px 10px 6px 10px;
    border-radius: 16px;
    width: fit-content;
  }
  .message {
    align-self: flex-end;
    width: fit-content;
    max-width: 70%;
  }
  .video-height {
    height: 150px;
  }
  .image-height {
    height: 384px;
  }
  .image {
    /*max-width: 256px;
    max-height: 384px;*/
    object-fit: contain;
    height: 384px;
    width: auto;
  }
  /* .video {
    width: 256px;
    height: fit-content;
  } */
</style>
